import {SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {AS_HALF_PRODUCT_QUANTITY, PdpWebviewEvents} from 'constants/recommendation';
import {BASKET_UPDATE_ITEM_TRIGGER_TYPE} from 'contexts/analytics-context';
import {getWholeNumberOrTwoDecimal} from 'utils/common/shopify.util';
import {findNearestValueByMOQ} from 'utils/recommendation-utils/recommendations';

const INCREMENT_DECREMENT_INTERVAL = 200;
const SINGLE_PRESS_THRESHOLD = 300;
const DEBOUNCE_DELAY = 100;

type QuantityPickerProps = {
  disabled: boolean;
  initialValue: number;
  max: number;
  onIncrement: (e: SyntheticEvent) => void;
  onDecrement: (e: SyntheticEvent) => void;
  onChange: (quantity: number, actionType: string) => Promise<boolean>;
  productId?: string;
  showKg?: boolean;
  moq?: number;
  isPDP?: boolean;
  isFavList?: boolean;
  isAddToCart?: boolean;
  enablePartialQuantity?: boolean;
  restrictDeleteFunctionality?: boolean;
  onDelete?: () => void;
  size?: 'mini' | 'small' | 'medium' | 'large';
};

export const useQuantityPicker = (props: QuantityPickerProps) => {
  const {
    initialValue,
    max,
    moq = 1,
    showKg,
    enablePartialQuantity,
    restrictDeleteFunctionality = false,
    onChange,
    onIncrement,
    onDecrement,
    onDelete,
    disabled
  } = props;

  const [value, setValue] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const isPressAndHoldEvent = useRef(false);
  const buttonPressedRef = useRef(false);
  const incrementIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const decrementIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef(0);
  const lastClickTimeRef = useRef(0);

  const minimumQuantity = useMemo(
    () => (enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : showKg ? moq : restrictDeleteFunctionality ? 0 : 1),
    [enablePartialQuantity, showKg, moq, restrictDeleteFunctionality]
  );

  const maximumQuantity = useMemo(() => (showKg ? max * moq : max), [showKg, max, moq]);

  const formattedValue = useMemo(() => (showKg ? parseFloat(value.toString()) : Number(value)), [showKg, value]);

  useEffect(() => {
    if (!isUpdating && initialValue !== undefined) {
      setValue(getWholeNumberOrTwoDecimal(!showKg ? initialValue : initialValue * moq));
    }
  }, [initialValue, moq, restrictDeleteFunctionality, showKg, isUpdating]);

  useEffect(() => {
    return () => {
      clearInterval(incrementIntervalRef.current as NodeJS.Timeout);
      clearInterval(decrementIntervalRef.current as NodeJS.Timeout);
      buttonPressedRef.current = false;
      isPressAndHoldEvent.current = false;
    };
  }, []);

  const clearIncrementInterval = useCallback(() => {
    if (incrementIntervalRef.current) {
      clearInterval(incrementIntervalRef.current);
      incrementIntervalRef.current = null;
    }
  }, []);

  const clearDecrementInterval = useCallback(() => {
    if (decrementIntervalRef.current) {
      clearInterval(decrementIntervalRef.current);
      decrementIntervalRef.current = null;
    }
  }, []);

  const updateQuantityOnRelease = useCallback(
    async (actionType?: string) => {
      if (isUpdating || disabled) return;

      setIsUpdating(true);
      try {
        clearIncrementInterval();
        clearDecrementInterval();

        let valueInKg = value;
        if (enablePartialQuantity) {
          valueInKg = findNearestValueByMOQ(AS_HALF_PRODUCT_QUANTITY, value);
        } else if (showKg) {
          valueInKg = findNearestValueByMOQ(moq, value);
        }

        const updated = await onChange(showKg ? valueInKg / moq : valueInKg, actionType || '');
        if (!updated || showKg) setValue(valueInKg);
      } finally {
        setIsUpdating(false);
      }
    },
    [
      value,
      showKg,
      moq,
      enablePartialQuantity,
      onChange,
      clearDecrementInterval,
      clearIncrementInterval,
      isUpdating,
      disabled
    ]
  );

  useEffect(() => {
    if (
      (decrementIntervalRef.current && value === minimumQuantity) ||
      (incrementIntervalRef.current && value >= maximumQuantity)
    ) {
      updateQuantityOnRelease();
    }
  }, [value, maximumQuantity, minimumQuantity, updateQuantityOnRelease]);

  const incrementCounter = useCallback(() => {
    if (isUpdating || disabled) return;
    setValue((prev) => {
      const newValue = prev >= maximumQuantity ? maximumQuantity : getWholeNumberOrTwoDecimal(prev + minimumQuantity);
      return newValue;
    });
  }, [maximumQuantity, minimumQuantity, isUpdating, disabled]);

  const decrementCounter = useCallback(() => {
    if (isUpdating || disabled) return;
    setValue((prev) => {
      const newValue = prev <= minimumQuantity ? minimumQuantity : getWholeNumberOrTwoDecimal(prev - minimumQuantity);
      return newValue;
    });
  }, [minimumQuantity, isUpdating, disabled]);

  const handleIncrementMouseDown = useCallback(() => {
    if (isUpdating || disabled) return;
    clearIncrementInterval();
    buttonPressedRef.current = true;
    startTimeRef.current = Date.now();
    incrementIntervalRef.current = setInterval(incrementCounter, INCREMENT_DECREMENT_INTERVAL);
  }, [clearIncrementInterval, incrementCounter, isUpdating, disabled]);

  const handleDecrementMouseDown = useCallback(
    (e: SyntheticEvent) => {
      if (isUpdating || disabled) return;

      if (value === minimumQuantity) {
        onDecrement(e);
      } else {
        clearDecrementInterval();
        buttonPressedRef.current = true;
        startTimeRef.current = Date.now();
        decrementIntervalRef.current = setInterval(decrementCounter, INCREMENT_DECREMENT_INTERVAL);
      }
    },
    [value, minimumQuantity, onDecrement, clearDecrementInterval, decrementCounter, isUpdating, disabled]
  );

  const handleMouseUp = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      if (disabled) return;

      clearIncrementInterval();
      clearDecrementInterval();
      buttonPressedRef.current = false;

      const timeDiff = Date.now() - startTimeRef.current;
      if (timeDiff >= SINGLE_PRESS_THRESHOLD) {
        isPressAndHoldEvent.current = true;
        await updateQuantityOnRelease(BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON_HOLD);
      }
    },
    [clearIncrementInterval, clearDecrementInterval, updateQuantityOnRelease, disabled]
  );

  const handleContextMenu = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
  }, []);

  const handleDecrementMouseLeave = useCallback(() => {
    if (buttonPressedRef.current) clearDecrementInterval();
    buttonPressedRef.current = false;
    isPressAndHoldEvent.current = false;
  }, [clearDecrementInterval]);

  const handleIncrementMouseLeave = useCallback(() => {
    if (buttonPressedRef.current) clearIncrementInterval();
    buttonPressedRef.current = false;
    isPressAndHoldEvent.current = false;
  }, [clearIncrementInterval]);

  const debouncedIncrement = useCallback(
    (e: SyntheticEvent) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < DEBOUNCE_DELAY) return;
      lastClickTimeRef.current = now;

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({type: PdpWebviewEvents.HANDLE_INCREMENT, product: null}));
      }

      if (!isPressAndHoldEvent.current && !isUpdating) {
        onIncrement(e);
      }
      isPressAndHoldEvent.current = false;
    },
    [onIncrement, isUpdating]
  );

  const debouncedDecrement = useCallback(
    (e: SyntheticEvent) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < DEBOUNCE_DELAY) return;
      lastClickTimeRef.current = now;

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({type: PdpWebviewEvents.HANDLE_DECREMENT, product: null}));
      }

      if (!isPressAndHoldEvent.current && !isUpdating) {
        onDecrement(e);
      }
      isPressAndHoldEvent.current = false;
    },
    [onDecrement, isUpdating]
  );

  const handleDelete = useCallback(() => {
    if (!isUpdating) {
      onDelete?.();
    }
  }, [onDelete, isUpdating]);

  return {
    formattedValue,
    minimumQuantity,
    maximumQuantity,
    isUpdating,
    handleDelete,
    handleDecrement: debouncedDecrement,
    handleDecrementMouseDown,
    handleMouseUp,
    handleDecrementMouseLeave,
    handleContextMenu,
    handleIncrement: debouncedIncrement,
    handleIncrementMouseDown,
    handleIncrementMouseLeave
  };
};
